import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Printer,
  PaperPlaneTilt,
  FilePdf,
  Trash,
  Prohibit,
  CurrencyDollar,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InvoicePrintout } from "@/components/InvoicePrintout";
import { DateField } from "@/components/DateField";
import { INVOICE_DOT, INVOICE_PLAN, money, day } from "@/pages/Invoices";
import { api, API, downloadFile, formatApiErrorDetail } from "@/lib/api";

const FALLBACK_METHODS = ["Check", "Cash", "ACH", "Credit card", "Other"];
const today = () => new Date().toISOString().slice(0, 10);

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [numCheck, setNumCheck] = useState(null);
  const [methods, setMethods] = useState(FALLBACK_METHODS);
  const [pay, setPay] = useState({
    amount: "",
    method: FALLBACK_METHODS[0],
    reference: "",
    note: "",
    at: today(),
  });

  useEffect(() => {
    api
      .get("/settings/branding")
      .then(({ data }) => setHasLogo(!!data.logo))
      .catch(() => setHasLogo(false));
    api
      .get("/settings/invoicing")
      .then(({ data }) => {
        const list = data.payment_methods?.length ? data.payment_methods : FALLBACK_METHODS;
        setMethods(list);
        setPay((cur) => ({ ...cur, method: list[0] }));
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
    setPay((cur) => ({ ...cur, amount: String(data.balance || "") }));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fail = (err, fallback) =>
    toast.error(formatApiErrorDetail(err.response?.data?.detail) || fallback);

  const doResync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/qbo-resync`);
      toast.success(`Synced to QuickBooks (QB #${data.qbo_id})`);
      await load();
    } catch (err) {
      fail(err, "Resync to QuickBooks failed");
    }
    setBusy(false);
  };

  // Warn before pushing when the app's number is already taken in QuickBooks.
  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/invoices/${id}/qbo-number-check`);
      if (data.exists) {
        setNumCheck({ ...data, newCode: `${inv.code}-2` });
        setBusy(false);
        return;
      }
    } catch {
      /* check is advisory — fall through to the sync */
    }
    await doResync();
  };

  const renumberAndSync = async () => {
    const code = (numCheck?.newCode || "").trim();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${id}`, { code });
      setInv((cur) => ({ ...cur, ...data }));
      setNumCheck(null);
    } catch (err) {
      fail(err, "Could not change the invoice number");
      setBusy(false);
      return;
    }
    await doResync();
  };

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${id}`, body);
      setInv((cur) => ({ ...cur, ...data }));
    } catch (err) {
      fail(err, "Could not save the invoice");
    }
    setBusy(false);
  };

  const record = async () => {
    if (!Number(pay.amount)) {
      toast.error("Enter the amount received");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/payments`, {
        amount: Number(pay.amount),
        method: pay.method,
        reference: pay.reference,
        note: pay.note,
        at: pay.at ? `${pay.at}T12:00:00+00:00` : "",
      });
      setInv((cur) => ({ ...cur, ...data }));
      setPay({
        amount: String(data.balance || ""),
        method: pay.method,
        reference: "",
        note: "",
        at: today(),
      });
      toast.success("Payment recorded");
    } catch (err) {
      fail(err, "Could not record that payment");
    }
    setBusy(false);
  };

  const removePayment = async (pid) => {
    const { data } = await api.delete(`/invoices/${id}/payments/${pid}`);
    setInv((cur) => ({ ...cur, ...data }));
    toast.success("Payment removed");
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      setInv((cur) => ({ ...cur, ...data }));
      toast.success(
        `Emailed to ${data.customer_email}${data.pdf ? " with the PDF attached" : ""}`,
      );
    } catch (err) {
      fail(err, "Could not email the invoice");
    }
    setBusy(false);
  };

  const voidIt = async () => {
    if (!window.confirm("Void this invoice? It disappears from the portal."))
      return;
    const { data } = await api.post(`/invoices/${id}/void`);
    setInv((cur) => ({ ...cur, ...data }));
  };

  const remove = async () => {
    if (!window.confirm("Delete this invoice for good?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    navigate("/invoices");
  };

  if (!inv)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Invoice" />
      </div>
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={inv.code} />
      <main className="relative z-10 pb-28 pt-6">
        <button
          type="button"
          data-testid="invoice-back"
          onClick={() => navigate("/invoices")}
          className="no-print flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Invoices
        </button>

        <div
          className="no-print mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="invoice-header-card"
        >
          <div className="flex flex-wrap items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono-data text-[11px] text-zinc-500">
                  {inv.code}
                </span>
                <span
                  data-testid="invoice-status-chip"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-200"
                >
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                      INVOICE_DOT[inv.status] || INVOICE_DOT.Draft
                    }`}
                  />
                  {inv.status}
                </span>
                {INVOICE_PLAN[inv.kind] && (
                  <span
                    data-testid="invoice-plan-badge"
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${INVOICE_PLAN[inv.kind].cls}`}
                  >
                    {INVOICE_PLAN[inv.kind].label}
                  </span>
                )}
              </div>
              <h1
                className="mt-2 truncate text-2xl font-bold tracking-tight"
                data-testid="invoice-title"
              >
                {inv.title}
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                {inv.customer_id ? (
                  <Link
                    to={`/customers/${inv.customer_id}`}
                    className="hover:text-indigo-300"
                    data-testid="invoice-account-link"
                  >
                    {inv.customer_company || inv.customer_name || "Account"}
                  </Link>
                ) : (
                  inv.customer_company || inv.customer_name || "No account"
                )}
                {inv.project_id && (
                  <>
                    {" · "}
                    <Link
                      to={`/projects/${inv.project_id}`}
                      className="hover:text-indigo-300"
                      data-testid="invoice-project-link"
                    >
                      {inv.project_name || "Project"}
                    </Link>
                  </>
                )}
                {inv.quote_id && (
                  <>
                    {" · "}
                    <Link
                      to={`/quotes/${inv.quote_id}`}
                      className="hover:text-indigo-300"
                      data-testid="invoice-quote-link"
                    >
                      {inv.quote_code || "Quote"}
                    </Link>
                  </>
                )}
              </p>
            </div>
            <div className="grid w-full gap-3 text-left sm:w-auto sm:text-right">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Balance due
                </div>
                <div
                  className="font-mono-data text-3xl font-bold"
                  data-testid="invoice-balance"
                >
                  {money(inv.balance)}
                </div>
                <div className="text-xs text-zinc-500">
                  {money(inv.paid)} paid of {money(inv.amount)} · due{" "}
                  {day(inv.due_date)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
            <Button
              data-testid="invoice-print"
              onClick={() => window.print()}
              className="h-12 w-full bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 sm:w-auto"
            >
              <Printer size={15} weight="bold" className="mr-2" /> Print / PDF
            </Button>
            <Button
              data-testid="invoice-download-pdf"
              variant="outline"
              onClick={() =>
                downloadFile(
                  `/invoices/${id}/pdf`,
                  `Invoice-${inv.code}.pdf`,
                ).catch(() => toast.error("Could not build the PDF"))
              }
              className="h-12 w-full border-zinc-800 bg-zinc-950 text-[11px] font-bold uppercase tracking-widest sm:w-auto"
            >
              <FilePdf size={15} weight="bold" className="mr-2" /> Download PDF
            </Button>
            <Button
              data-testid="invoice-send"
              variant="outline"
              disabled={busy}
              onClick={send}
              className="h-12 w-full border-zinc-800 bg-zinc-950 text-[11px] font-bold uppercase tracking-widest sm:w-auto"
            >
              <PaperPlaneTilt size={15} weight="bold" className="mr-2" /> Email
              invoice
            </Button>
            <Button
              data-testid="invoice-void"
              variant="outline"
              disabled={busy || inv.status === "Void"}
              onClick={voidIt}
              className="h-12 w-full border-zinc-800 bg-zinc-950 text-[11px] font-bold uppercase tracking-widest text-amber-300 sm:w-auto"
            >
              <Prohibit size={15} weight="bold" className="mr-2" /> Void
            </Button>
            <Button
              data-testid="invoice-delete"
              variant="outline"
              onClick={remove}
              className="h-12 w-full border-red-500/30 bg-zinc-950 text-[11px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10 sm:ml-auto sm:w-auto"
            >
              <Trash size={15} weight="bold" className="mr-2" /> Delete
            </Button>
          </div>
          {inv.qbo_enabled && (
            <div
              className="mt-3 flex flex-wrap items-center gap-3 border-t border-zinc-800 pt-4"
              data-testid="invoice-qbo-status"
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                QuickBooks
              </span>
              {inv.qbo?.status === "synced" ? (
                <>
                  <span className="text-xs font-semibold text-emerald-400">
                    Synced ✓{inv.qbo.qbo_id ? ` · QB #${inv.qbo.qbo_id}` : ""}
                  </span>
                  {inv.qbo.linked_existing && (
                    <span
                      data-testid="invoice-qbo-linked-badge"
                      title={`This number already existed in QuickBooks, so the app linked to that invoice (QB #${inv.qbo.qbo_id}) instead of creating a duplicate.`}
                      className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-sky-300"
                    >
                      Linked to existing QuickBooks invoice
                    </span>
                  )}
                  {inv.qbo.doc_number && inv.qbo.doc_number !== inv.code && (
                    <span data-testid="invoice-qbo-docnumber" className="text-[10px] text-zinc-500">
                      QB number {inv.qbo.doc_number}
                    </span>
                  )}
                  {inv.balance > 0 && inv.status !== "Void" && (
                    <Button
                      data-testid="invoice-qbo-check-payments"
                      variant="outline"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const { data } = await api.post(`/invoices/${id}/qbo/refresh-payments`);
                          if (data.recorded > 0) {
                            toast.success(`Recorded ${money(data.recorded)} from QuickBooks`);
                            setInv((cur) => ({ ...cur, ...data.invoice }));
                          } else toast("No new QuickBooks payments on this invoice");
                        } catch (err) {
                          toast.error(
                            formatApiErrorDetail(err.response?.data?.detail) ||
                              "Could not check QuickBooks",
                          );
                        }
                        setBusy(false);
                      }}
                      className="h-8 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                    >
                      <ArrowsClockwise size={13} weight="bold" className="mr-1.5" /> Check
                      QuickBooks for payments
                    </Button>
                  )}
                </>
              ) : inv.qbo?.status === "failed" ? (
                <>
                  <span className="text-xs font-semibold text-red-400">
                    Sync failed
                  </span>
                  {inv.qbo.error && (
                    <span
                      className="max-w-md truncate text-xs text-zinc-500"
                      data-testid="invoice-qbo-error"
                      title={inv.qbo.error}
                    >
                      {inv.qbo.error}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-zinc-500">Not synced yet</span>
              )}
              {inv.qbo?.status !== "synced" && (
                <Button
                  data-testid="invoice-qbo-resync"
                  variant="outline"
                  disabled={busy}
                  onClick={resync}
                  className="h-8 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                >
                  <ArrowsClockwise size={13} weight="bold" className="mr-1.5" />{" "}
                  Resync
                </Button>
              )}
              <Dialog open={!!numCheck} onOpenChange={(o) => !o && setNumCheck(null)}>
                <DialogContent className="border-zinc-800 bg-zinc-900 sm:max-w-md" data-testid="invoice-qbo-number-dialog">
                  <DialogHeader>
                    <DialogTitle>Invoice number already used in QuickBooks</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-zinc-300">
                    <span className="font-mono-data">{numCheck?.code}</span> is already assigned to QuickBooks invoice
                    #{numCheck?.txn_id}
                    {numCheck?.qbo_customer ? ` for ${numCheck.qbo_customer}` : ""}
                    {numCheck?.same_customer ? " (same customer — syncing will link to it)." : "."}
                  </p>
                  <div>
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Use a different number</div>
                    <Input
                      data-testid="invoice-qbo-new-number"
                      value={numCheck?.newCode || ""}
                      onChange={(e) => setNumCheck((c) => ({ ...c, newCode: e.target.value }))}
                      className="h-10 border-zinc-800 bg-zinc-950 font-mono-data"
                    />
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      data-testid="invoice-qbo-sync-anyway"
                      variant="outline"
                      disabled={busy}
                      onClick={() => { setNumCheck(null); doResync(); }}
                      className="h-9 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                    >
                      {numCheck?.same_customer ? "Link to existing" : "Sync anyway"}
                    </Button>
                    <Button
                      data-testid="invoice-qbo-renumber-sync"
                      disabled={busy || !(numCheck?.newCode || "").trim()}
                      onClick={renumberAndSync}
                      className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest hover:bg-indigo-400"
                    >
                      Save number &amp; sync
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div id="print-area" className="min-w-0">
            <InvoicePrintout
              invoice={inv}
              template={inv.template}
              logoUrl={hasLogo ? `${API}/public/logo` : ""}
            />
          </div>

          <div className="no-print space-y-4">
            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="invoice-payment-card"
            >
              <h2 className="flex items-center justify-between gap-2 text-sm font-semibold uppercase tracking-widest text-zinc-300">
                <span className="flex items-center gap-2">
                  <CurrencyDollar
                    size={16}
                    weight="bold"
                    className="text-emerald-300"
                  />
                  Record a payment
                </span>
                <Link
                  to="/settings/invoicing"
                  data-testid="invoice-payment-methods-link"
                  className="text-[10px] font-bold tracking-widest text-indigo-300 hover:underline"
                >
                  Edit options
                </Link>
              </h2>
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Amount received
                    </span>
                    <Input
                      data-testid="invoice-payment-amount"
                      type="number"
                      step="0.01"
                      value={pay.amount}
                      onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                      className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Received on
                    </span>
                    <DateField
                      testid="invoice-payment-date"
                      value={pay.at}
                      className="mt-1"
                      onChange={(v) => setPay({ ...pay, at: v })}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Paid by
                  </span>
                  <Select
                    value={pay.method}
                    onValueChange={(v) => setPay({ ...pay, method: v })}
                  >
                    <SelectTrigger
                      data-testid="invoice-payment-method"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`invoice-payment-method-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Check no. / reference
                  </span>
                  <Input
                    data-testid="invoice-payment-reference"
                    value={pay.reference}
                    onChange={(e) =>
                      setPay({ ...pay, reference: e.target.value })
                    }
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Note
                  </span>
                  <Input
                    data-testid="invoice-payment-note"
                    value={pay.note}
                    placeholder="Optional"
                    onChange={(e) => setPay({ ...pay, note: e.target.value })}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <Button
                  data-testid="invoice-payment-save"
                  disabled={busy || inv.status === "Void"}
                  onClick={record}
                  className="h-11 w-full bg-emerald-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-emerald-400"
                >
                  Record payment
                </Button>
              </div>

              {(inv.payments || []).length > 0 && (
                <div className="mt-4 divide-y divide-zinc-800 border-t border-zinc-800 pt-2">
                  {inv.payments.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`invoice-payment-${p.id}`}
                      className="flex items-center gap-2 py-2.5 text-sm"
                    >
                      <span className="font-mono-data font-bold">
                        {money(p.amount)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs text-zinc-500">
                        {p.method}
                        {p.reference ? ` · ${p.reference}` : ""} · {day(p.at)}
                        {p.note ? ` · ${p.note}` : ""}
                      </span>
                      {inv.qbo_enabled && p.qbo?.status === "synced" && (
                        <span
                          data-testid={`invoice-payment-qbo-ok-${p.id}`}
                          title={`QuickBooks payment #${p.qbo.qbo_id}`}
                          className="shrink-0 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-400"
                        >
                          QB ✓
                        </span>
                      )}
                      {inv.qbo_enabled && p.qbo?.status === "failed" && (
                        <span
                          data-testid={`invoice-payment-qbo-error-${p.id}`}
                          title={p.qbo.error}
                          className="max-w-[180px] shrink-0 truncate rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-300"
                        >
                          QB: {p.qbo.error || "sync failed"}
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`invoice-payment-delete-${p.id}`}
                        onClick={() => removePayment(p.id)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="invoice-edit-card"
            >
              <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-300">
                Invoice details
              </h2>
              <div className="mt-4 space-y-3">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Title
                  </span>
                  <Input
                    data-testid="invoice-edit-title"
                    value={inv.title}
                    onChange={(e) =>
                      setInv((cur) => ({ ...cur, title: e.target.value }))
                    }
                    onBlur={(e) => patch({ title: e.target.value })}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Amount
                    </span>
                    <Input
                      data-testid="invoice-edit-amount"
                      type="number"
                      step="0.01"
                      value={inv.amount}
                      onChange={(e) =>
                        setInv((cur) => ({ ...cur, amount: e.target.value }))
                      }
                      onBlur={(e) => patch({ amount: Number(e.target.value) })}
                      className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Due date
                    </span>
                    <DateField
                      testid="invoice-edit-due"
                      value={inv.due_date || ""}
                      placeholder="Due on install"
                      className="mt-1"
                      onChange={(v) => {
                        setInv({ ...inv, due_date: v });
                        patch({ due_date: v });
                      }}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    PO number
                  </span>
                  <Input
                    data-testid="invoice-edit-po"
                    value={inv.po_number || ""}
                    onChange={(e) =>
                      setInv((cur) => ({ ...cur, po_number: e.target.value }))
                    }
                    onBlur={(e) => patch({ po_number: e.target.value })}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Online payment link
                  </span>
                  <Input
                    data-testid="invoice-edit-pay-link"
                    placeholder="https://…  (QuickBooks or any payment page)"
                    value={inv.pay_link || ""}
                    onChange={(e) =>
                      setInv((cur) => ({ ...cur, pay_link: e.target.value }))
                    }
                    onBlur={(e) => patch({ pay_link: e.target.value })}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                  <span className="mt-1 block text-[11px] text-zinc-600">
                    Shown as a "Pay online" button in the customer portal. Leave
                    blank to use the QuickBooks payment page automatically once
                    QuickBooks is connected.
                  </span>
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Payment terms
                  </span>
                  <Input
                    data-testid="invoice-edit-terms"
                    value={inv.terms || ""}
                    onChange={(e) =>
                      setInv((cur) => ({ ...cur, terms: e.target.value }))
                    }
                    onBlur={(e) => patch({ terms: e.target.value })}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Note on the invoice
                  </span>
                  <textarea
                    data-testid="invoice-edit-notes"
                    rows={3}
                    value={inv.notes || ""}
                    onChange={(e) =>
                      setInv((cur) => ({ ...cur, notes: e.target.value }))
                    }
                    onBlur={(e) => patch({ notes: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400"
                  />
                </label>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
